import { useEffect, type FC } from "react"

// Single array for all colors
const ALL_COLORS = [
  "#051077", "#0e2b9f", "#0b0f6c", "#290da7", "#31158f",
  "#73043f", "#810c4a", "#880552", "#d01645", "#6c2048", "#e7422c",
  "#8a0749", "#cd4053", "#d94551", "#4f0479", "#401197", "#3e1aaa",
  "#4e1976", "#51056d"
]

export const DEFAULT_COLORS = ALL_COLORS

/**
 * Shuffles an array of colors using the Fisher-Yates algorithm
 * @param colors - Array of color strings to shuffle
 * @returns A new shuffled array of colors
 */
const shuffleColors = (colors: string[]): string[] => {
  const shuffledColors = [...colors]
  for (let i = shuffledColors.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    // Safe array element swapping - we know indices are valid within the loop bounds
    const temp = shuffledColors[i]
    const swapElement = shuffledColors[j]
    if (temp !== undefined && swapElement !== undefined) {
      shuffledColors[i] = swapElement
      shuffledColors[j] = temp
    }
  }
  return shuffledColors.filter((color): color is string => typeof color === 'string')
}

/**
 * Interpolates between two hex colors by a given factor
 * @param color1 - Starting hex color (e.g., "#ff0000")
 * @param color2 - Ending hex color (e.g., "#0000ff")
 * @param factor - Interpolation factor between 0 and 1
 * @returns Interpolated hex color string
 */
const interpolateColors = (color1: string, color2: string, factor: number): string => {
  const r1 = parseInt(color1.slice(1, 3), 16)
  const g1 = parseInt(color1.slice(3, 5), 16)
  const b1 = parseInt(color1.slice(5, 7), 16)
  
  const r2 = parseInt(color2.slice(1, 3), 16)
  const g2 = parseInt(color2.slice(3, 5), 16)
  const b2 = parseInt(color2.slice(5, 7), 16)
  
  const r = Math.round(r1 + factor * (r2 - r1))
  const g = Math.round(g1 + factor * (g2 - g1))
  const b = Math.round(b1 + factor * (b2 - b1))
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

type ColorGradientSelectorProps = {
  colors?: string[]
  lineCount: number
  onColorChange: (gradients: string[][]) => void
}

export const ColorGradientSelector: FC<ColorGradientSelectorProps> = ({
  colors = DEFAULT_COLORS,
  lineCount,
  onColorChange,
}) => {
  useEffect(() => {
    const gradients: string[][] = []
    let shuffledColors = shuffleColors(colors)
    let colorIndex = 0

    /**
     * Gets the next color from the shuffled array, reshuffling when exhausted
     * @returns A color string from the available colors
     */
    const getNextColor = (): string => {
      if (colorIndex >= shuffledColors.length) {
        shuffledColors = shuffleColors(colors)
        colorIndex = 0
      }
      // Safe access - we ensure colorIndex is always valid and colors array is non-empty
      const color = shuffledColors[colorIndex++]
      return color ?? colors[0] ?? "#000000" // Fallback to first color or black if somehow undefined
    }

    for (let i = 0; i < lineCount; i++) {
      const gradient: string[] = []
      const startColor = getNextColor()
      const endColor = getNextColor()
      
      // Generate a smooth gradient with more interpolated colors
      const steps = 5 // Increase this for smoother gradients
      for (let j = 0; j <= steps; j++) {
        const factor = j / steps
        const interpolatedColor = interpolateColors(startColor, endColor, factor)
        gradient.push(interpolatedColor)
      }
      
      gradients.push(gradient)
    }

    // console.log("Generated gradients:", gradients); // Add logging here
    onColorChange(gradients)
  }, [colors, lineCount, onColorChange])

  return null
}